import axios from 'axios';
import { ApiResponse, LoginData, RegisterData, CreateProjectData, Project, User, ProjectStatus, TableSchema, Backup, BackupStats, PITRConfig, BaseBackup, PITRRestore, PITRStats, RecoveryWindow } from '@/types';
import Cookies from 'js-cookie';
import { setAuthTokens } from '@/lib/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies cross-domain
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = Cookies.get('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn('No access token found in cookies');
  }
  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = Cookies.get('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refreshToken,
          });

          const { tokens } = response.data;
          setAuthTokens(tokens);

          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          Cookies.remove('accessToken');
          Cookies.remove('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  async login(data: LoginData): Promise<ApiResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, data);
    return response.data;
  },

  async register(data: RegisterData): Promise<ApiResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/v1/auth/register`, data);
    return response.data;
  },

  async logout(refreshToken: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/v1/auth/logout`, { refreshToken });
  },

  async getProfile(): Promise<ApiResponse<User>> {
    const response = await api.get('/api/v1/auth/profile');
    return response.data;
  },

  async updateProfile(data: { name?: string }): Promise<ApiResponse<User>> {
    const response = await api.put('/api/v1/auth/profile', data);
    return response.data;
  },

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<ApiResponse> {
    const response = await api.post('/api/v1/auth/change-password', data);
    return response.data;
  },
};

export const projectsApi = {
  async list(): Promise<ApiResponse<Project[]>> {
    const response = await api.get('/api/v1/projects');
    return response.data;
  },

  async get(id: string): Promise<ApiResponse<ProjectStatus>> {
    const response = await api.get(`/api/v1/projects/${id}`);
    return response.data;
  },

  async create(data: CreateProjectData): Promise<ApiResponse<Project>> {
    const response = await api.post('/api/v1/projects', data);
    return response.data;
  },

  async delete(id: string): Promise<ApiResponse> {
    const response = await api.delete(`/api/v1/projects/${id}`);
    return response.data;
  },

  async regenerateApiKey(id: string): Promise<ApiResponse<{ api_key: string }>> {
    const response = await api.post(`/api/v1/projects/${id}/regenerate-key`);
    return response.data;
  },

  async testConnection(id: string): Promise<ApiResponse<{ connected: boolean; database_size?: string; connection_count?: number }>> {
    const response = await api.get(`/api/v1/projects/${id}/test-connection`);
    return response.data;
  },
};

export const restApi = {
  async listTables(projectId: string): Promise<{ tables: string[] }> {
    const response = await api.get(`/rest/v1/projects/${projectId}/tables`);
    return response.data;
  },

  async getTableSchema(projectId: string, tableName: string): Promise<TableSchema> {
    const response = await api.get(`/rest/v1/projects/${projectId}/tables/${tableName}/schema`);
    return response.data;
  },

  async queryTable(projectId: string, tableName: string, params: Record<string, any> = {}): Promise<any[]> {
    const response = await api.get(`/rest/v1/projects/${projectId}/${tableName}`, { params });
    return response.data;
  },

  async insertRow(projectId: string, tableName: string, data: Record<string, any>): Promise<any> {
    const response = await api.post(`/rest/v1/projects/${projectId}/${tableName}`, data);
    return response.data;
  },

  async updateRow(projectId: string, tableName: string, data: Record<string, any>, filters: Record<string, any>): Promise<any> {
    const response = await api.patch(`/rest/v1/projects/${projectId}/${tableName}`, data, { params: filters });
    return response.data;
  },

  async deleteRow(projectId: string, tableName: string, filters: Record<string, any>): Promise<any> {
    const response = await api.delete(`/rest/v1/projects/${projectId}/${tableName}`, { params: filters });
    return response.data;
  },
};

// RLS API functions
export const rlsApi = {
  async getPolicies(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/rls/projects/${projectId}/policies`);
    return response.data;
  },

  async getTables(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/rls/projects/${projectId}/tables`);
    return response.data;
  },

  async createPolicy(projectId: string, policyData: any): Promise<any> {
    const response = await api.post(`/api/v1/rls/projects/${projectId}/policies`, policyData);
    return response.data;
  },

  async deletePolicy(policyId: string): Promise<any> {
    const response = await api.delete(`/api/v1/rls/policies/${policyId}`);
    return response.data;
  },

  async toggleRLS(projectId: string, tableName: string, enable: boolean): Promise<any> {
    const response = await api.post(`/api/v1/rls/projects/${projectId}/rls/${enable ? 'enable' : 'disable'}`, {
      table_name: tableName
    });
    return response.data;
  },
};

// Real-time API functions
export const realtimeApi = {
  async getStatus(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/realtime/projects/${projectId}/status`);
    return response.data;
  },

  async enableRealtime(projectId: string, tableName: string, events: string[]): Promise<any> {
    const response = await api.post(`/api/v1/realtime/projects/${projectId}/enable`, {
      table_name: tableName,
      events
    });
    return response.data;
  },

  async disableRealtime(projectId: string, tableName: string): Promise<any> {
    const response = await api.post(`/api/v1/realtime/projects/${projectId}/disable`, {
      table_name: tableName
    });
    return response.data;
  },
};

export const replicasApi = {
  async get(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/replicas/projects/${projectId}`);
    return response.data;
  },

  async create(projectId: string, name: string): Promise<any> {
    const response = await api.post(`/api/v1/replicas/projects/${projectId}`, { name });
    return response.data;
  },

  async remove(projectId: string): Promise<any> {
    const response = await api.delete(`/api/v1/replicas/projects/${projectId}`);
    return response.data;
  },
};

// Schema API functions
export const schemaApi = {
  async getSchema(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/schema/projects/${projectId}`);
    return response.data;
  },

  async getSchemaSQL(projectId: string): Promise<string> {
    const response = await api.get(`/api/v1/schema/projects/${projectId}?format=sql`);
    return response.data;
  },

  async validateSQL(projectId: string, sql: string): Promise<any> {
    const response = await api.post(`/api/v1/schema/projects/${projectId}/validate`, { sql });
    return response.data;
  },

  async getTables(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/schema/projects/${projectId}/tables`);
    return response.data;
  },
};

// Migrations API functions
export const migrationsApi = {
  async getMigrations(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/migrations/projects/${projectId}`);
    return response.data;
  },

  async getHistory(projectId: string): Promise<any> {
    const response = await api.get(`/api/v1/migrations/projects/${projectId}`);
    return response.data;
  },

  async createMigration(projectId: string, migrationData: any): Promise<any> {
    const response = await api.post(`/api/v1/migrations/projects/${projectId}`, migrationData);
    return response.data;
  },

  async applyMigration(migrationId: string): Promise<any> {
    const response = await api.post(`/api/v1/migrations/${migrationId}/apply`);
    return response.data;
  },

  async rollbackMigration(migrationId: string): Promise<any> {
    const response = await api.post(`/api/v1/migrations/${migrationId}/rollback`);
    return response.data;
  },
};

// Monitoring API functions
export const monitoringApi = {
  async getHealth(): Promise<any> {
    const response = await api.get('/health/detailed');
    return response.data;
  },

  async getSystemStatus(): Promise<any> {
    const response = await api.get('/system/status');
    return response.data;
  },

  async getMetrics(): Promise<string> {
    const response = await api.get('/metrics');
    return response.data;
  },
};

// Query Builder API functions
export const queryBuilderApi = {
  async executeQuery(projectId: string, sql: string, params: any[] = []): Promise<any> {
    const response = await api.post(`/api/v1/query-builder/projects/${projectId}/execute`, {
      sql,
      params
    });
    return response.data;
  },

  async buildQuery(projectId: string, queryConfig: any): Promise<any> {
    const response = await api.post(`/api/v1/query-builder/projects/${projectId}/build`, queryConfig);
    return response.data;
  },

  async validateQuery(projectId: string, sql: string): Promise<any> {
    const response = await api.post(`/api/v1/query-builder/projects/${projectId}/validate`, {
      sql
    });
    return response.data;
  },
};

// Backups API functions
export const backupsApi = {
  async getBackups(projectId: string): Promise<ApiResponse<{ backups: Backup[]; stats: BackupStats; total: number }>> {
    const response = await api.get(`/api/v1/backups/projects/${projectId}`);
    return response.data;
  },

  async createBackup(projectId: string, data: { name: string; description?: string; format?: string }): Promise<ApiResponse<{ backup: Backup; execution_time_ms: number }>> {
    const response = await api.post(`/api/v1/backups/projects/${projectId}`, data);
    return response.data;
  },

  async getBackupStats(projectId: string): Promise<ApiResponse<BackupStats>> {
    const response = await api.get(`/api/v1/backups/projects/${projectId}/stats`);
    return response.data;
  },

  async getBackup(backupId: string): Promise<ApiResponse<Backup>> {
    const response = await api.get(`/api/v1/backups/${backupId}`);
    return response.data;
  },

  async restoreBackup(backupId: string): Promise<ApiResponse<{ backup: Backup; execution_time_ms: number }>> {
    const response = await api.post(`/api/v1/backups/${backupId}/restore`);
    return response.data;
  },

  async deleteBackup(backupId: string): Promise<ApiResponse> {
    const response = await api.delete(`/api/v1/backups/${backupId}`);
    return response.data;
  },
};

// PITR (Point-in-Time Recovery) API functions
export const pitrApi = {
  async getConfig(projectId: string): Promise<ApiResponse<{
    config: PITRConfig | null;
    stats: PITRStats;
    recovery_window: RecoveryWindow;
  }>> {
    const response = await api.get(`/api/v1/pitr/projects/${projectId}`);
    return response.data;
  },

  async enablePITR(projectId: string, data?: {
    base_backup_interval_hours?: number;
    wal_retention_hours?: number;
    max_base_backups?: number;
  }): Promise<ApiResponse<{ config: PITRConfig; error?: string }>> {
    const response = await api.post(`/api/v1/pitr/projects/${projectId}/enable`, data || {});
    return response.data;
  },

  async disablePITR(projectId: string): Promise<ApiResponse<{ config: PITRConfig }>> {
    const response = await api.post(`/api/v1/pitr/projects/${projectId}/disable`);
    return response.data;
  },

  async updateConfig(projectId: string, data: {
    base_backup_interval_hours?: number;
    wal_retention_hours?: number;
    max_base_backups?: number;
  }): Promise<ApiResponse<{ config: PITRConfig }>> {
    const response = await api.patch(`/api/v1/pitr/projects/${projectId}`, data);
    return response.data;
  },

  async getStats(projectId: string): Promise<ApiResponse<PITRStats>> {
    const response = await api.get(`/api/v1/pitr/projects/${projectId}/stats`);
    return response.data;
  },

  async getRecoveryWindow(projectId: string): Promise<ApiResponse<RecoveryWindow & {
    base_backups: Array<{
      id: string;
      completed_at: string;
      wal_start_lsn: string;
      wal_end_lsn: string;
    }>;
  }>> {
    const response = await api.get(`/api/v1/pitr/projects/${projectId}/recovery-window`);
    return response.data;
  },

  async getBaseBackups(projectId: string): Promise<ApiResponse<{
    base_backups: BaseBackup[];
    total: number;
  }>> {
    const response = await api.get(`/api/v1/pitr/projects/${projectId}/base-backups`);
    return response.data;
  },

  async createBaseBackup(projectId: string): Promise<ApiResponse<{
    base_backup: BaseBackup;
    execution_time_ms: number;
  }>> {
    const response = await api.post(`/api/v1/pitr/projects/${projectId}/base-backups`);
    return response.data;
  },

  async getBaseBackup(backupId: string): Promise<ApiResponse<BaseBackup>> {
    const response = await api.get(`/api/v1/pitr/base-backups/${backupId}`);
    return response.data;
  },

  async deleteBaseBackup(backupId: string): Promise<ApiResponse> {
    const response = await api.delete(`/api/v1/pitr/base-backups/${backupId}`);
    return response.data;
  },

  async restoreToPointInTime(projectId: string, targetTime: string): Promise<ApiResponse<{
    restore: PITRRestore;
    message?: string;
    error?: string;
  }>> {
    const response = await api.post(`/api/v1/pitr/projects/${projectId}/restore`, {
      target_time: targetTime,
    });
    return response.data;
  },

  async getRestoreHistory(projectId: string): Promise<ApiResponse<{
    restores: PITRRestore[];
    total: number;
  }>> {
    const response = await api.get(`/api/v1/pitr/projects/${projectId}/restores`);
    return response.data;
  },
};

export default api;